import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type SystemSettings as Settings } from '../api';
import { Alert, Badge, Button, Card, Flash, PageHeader, Skeleton } from '../components/ui';
import { TextField } from '../components/form';

/**
 * How the system behaves (SPEC §8 item 18).
 *
 * Deliberately a different screen from Form settings, which edits what the
 * *form* offers. These are two different jobs with two different blast radii:
 * getting a department wrong makes a dropdown untidy, getting the sending
 * identity wrong stops mail.
 *
 * Anything the server's configuration owns is shown here read-only rather than
 * left off. An administrator asking "why is no mail going out" should be able
 * to see that mail is switched off without a shell on the box, and equally
 * should not be handed a text box that pretends they can change it.
 */

type Draft = {
  mailFromName: string;
  mailReplyTo: string;
  nudgeIntervalHours: string;
  pdfHeaderText: string;
  pdfFooterText: string;
  retentionMonths: string;
  onsiteTechEmail: string;
};

const toDraft = (settings: Settings): Draft => ({
  onsiteTechEmail: settings.onsiteTechEmail ?? '',
  mailFromName: settings.mailFromName ?? '',
  mailReplyTo: settings.mailReplyTo ?? '',
  nudgeIntervalHours: String(settings.nudgeIntervalHours),
  pdfHeaderText: settings.pdfHeaderText ?? '',
  pdfFooterText: settings.pdfFooterText ?? '',
  retentionMonths: settings.retentionMonths === null ? '' : String(settings.retentionMonths),
});

export function SystemSettings() {
  const client = useQueryClient();
  const loaded = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seeded once the fetch lands. Held as strings because that is what inputs
  // are, and coercing on every keystroke makes a half-typed number vanish.
  useEffect(() => {
    if (loaded.data && draft === null) setDraft(toDraft(loaded.data.settings));
  }, [loaded.data, draft]);

  const save = useMutation({
    mutationFn: (next: Draft) =>
      api.saveSettings({
        mailFromName: next.mailFromName.trim() || null,
        mailReplyTo: next.mailReplyTo.trim() || null,
        nudgeIntervalHours: Number(next.nudgeIntervalHours),
        pdfHeaderText: next.pdfHeaderText.trim() || null,
        pdfFooterText: next.pdfFooterText.trim() || null,
        retentionMonths: next.retentionMonths.trim() === '' ? null : Number(next.retentionMonths),
        onsiteTechEmail: next.onsiteTechEmail.trim() || null,
      }),
    onSuccess: (result) => {
      setError(null);
      setDraft(toDraft(result.settings));
      void client.invalidateQueries({ queryKey: ['settings'] });
      setFlash(
        result.changed.length === 0
          ? 'Nothing had changed.'
          : `Saved ${result.changed.length} change${result.changed.length === 1 ? '' : 's'}.`,
      );
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save those settings.'),
  });

  if (loaded.isPending || draft === null) {
    return (
      <div>
        <PageHeader title="System settings" description="How this system behaves." />
        <Card className="space-y-4 p-6">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </Card>
      </div>
    );
  }
  if (loaded.isError) {
    return <Alert>{loaded.error instanceof ApiError ? loaded.error.message : 'Could not open settings.'}</Alert>;
  }

  const { settings, limits, environment } = loaded.data;
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(settings));

  const hours = Number(draft.nudgeIntervalHours);
  const hoursError =
    draft.nudgeIntervalHours.trim() === '' || !Number.isInteger(hours)
      ? 'Whole hours, please.'
      : hours < limits.nudgeIntervalHours.min || hours > limits.nudgeIntervalHours.max
        ? `Between ${limits.nudgeIntervalHours.min} and ${limits.nudgeIntervalHours.max} hours.`
        : undefined;

  const months = Number(draft.retentionMonths);
  const monthsError =
    draft.retentionMonths.trim() === ''
      ? undefined
      : !Number.isInteger(months) || months < limits.retentionMonths.min || months > limits.retentionMonths.max
        ? `Between ${limits.retentionMonths.min} and ${limits.retentionMonths.max} months, or empty to keep records indefinitely.`
        : undefined;

  const blocked = Boolean(hoursError || monthsError);

  return (
    <div>
      <PageHeader
        title="System settings"
        description="How this system behaves. What the form itself offers is under Form settings."
        actions={
          settings.updatedBy ? (
            <Badge tone="neutral">
              Last changed by {settings.updatedBy},{' '}
              {new Date(settings.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
            </Badge>
          ) : undefined
        }
      />

      {flash && <Flash message={flash} onDismiss={() => setFlash(null)} />}
      {error && (
        <div className="mb-6">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="space-y-6">
        <Section
          title="Outgoing mail"
          description="Notifications and reminders are sent from a shared mailbox over Microsoft Graph."
        >
          <Fixed
            label="Sending address"
            value={environment.mailFromAddress ?? 'Not configured'}
            note="Set on the server. It has to match the Exchange access policy that confines this app to one mailbox, so changing it here would only produce mail Graph refuses to send."
          />
          <Fixed
            label="Mail"
            value={environment.mailEnabled ? 'Switched on' : 'Switched off: notifications are saved in the app only'}
            note="Set on the server, so that a test deployment cannot mail real people by accident."
          />
          <TextField
            label="Sender name"
            value={draft.mailFromName}
            onChange={(value) => set({ mailFromName: value })}
            placeholder="NHC IT Enrolment"
            maxLength={80}
            hint="Shown in front of the address in somebody's inbox."
          />
          <TextField
            label="Reply-to"
            type="email"
            value={draft.mailReplyTo}
            onChange={(value) => set({ mailReplyTo: value })}
            placeholder="it@newhorizoncopper.com"
            maxLength={200}
            hint="Where a reply should land. Leave empty and replies go back to the sending mailbox, which nobody reads."
          />
          <TextField
            label="Onsite tech mailbox"
            type="email"
            value={draft.onsiteTechEmail}
            onChange={(value) => set({ onsiteTechEmail: value })}
            placeholder="nhc-it@microtechnamibia.com"
            maxLength={200}
            hint="Where Push to onsite tech sends a request. Leave empty for nhc-it@microtechnamibia.com."
          />
        </Section>

        <MailDelivery mailEnabled={environment.mailEnabled} problem={environment.mailProblem} />

        <Section
          title="Reminders"
          description="Anybody waiting on a request can send a reminder to whoever it is sitting with."
        >
          <TextField
            label="Hours between reminders on one request"
            value={draft.nudgeIntervalHours}
            onChange={(value) => set({ nudgeIntervalHours: value })}
            error={hoursError}
            hint="The limit is per request, not per person, so a busy queue cannot turn into a mailstorm from several people at once."
          />
        </Section>

        <Section title="Signed PDF" description="A running header and footer on every page of the signed record.">
          {!environment.pdfAvailable && (
            <Alert tone="warning">
              No Chromium was found on the server, so PDFs cannot be rendered at the moment. The record is
              still served as a web page, and these settings will apply once it is installed.
            </Alert>
          )}
          <TextField
            label="Header line"
            value={draft.pdfHeaderText}
            onChange={(value) => set({ pdfHeaderText: value })}
            placeholder="New Horizon Copper: IT Enrolment"
            maxLength={120}
          />
          <TextField
            label="Footer line"
            value={draft.pdfFooterText}
            onChange={(value) => set({ pdfFooterText: value })}
            placeholder="Confidential: retain per IT policy"
            maxLength={120}
            hint="Page numbers are always printed beside this, so a printed record can be checked for completeness."
          />
        </Section>

        <Section
          title="Retention"
          description="How long a completed record should be kept, as company policy."
        >
          <TextField
            label="Months to keep a completed record"
            value={draft.retentionMonths}
            onChange={(value) => set({ retentionMonths: value })}
            error={monthsError}
            placeholder="Leave empty to keep indefinitely"
            hint="Recorded and shown here. Nothing is deleted automatically: a signed record is the answer to “what did we give that person?” years later, and a job that quietly removed them would be worse than not having one. Deleting on this schedule is a deliberate step, taken once NHC has agreed the policy."
          />
        </Section>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={() => save.mutate(draft)} disabled={!dirty || blocked || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(toDraft(settings))} disabled={save.isPending}>
            Discard
          </Button>
        )}
        {!dirty && <span className="text-sm text-slate-500 dark:text-slate-400">No unsaved changes.</span>}
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h2>
      <p className="mt-1 mb-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{description}</p>
      <div className="grid max-w-2xl gap-5">{children}</div>
    </Card>
  );
}

/**
 * Whether email actually leaves this system, answered from the app: a test
 * message to yourself with Graph's reply shown as it is, and the last emails
 * the system tried to send, each marked sent, failed with the reason, or
 * never sent because mail is off.
 */
function MailDelivery({ mailEnabled, problem }: { mailEnabled: boolean; problem: string | null }) {
  const log = useQuery({ queryKey: ['settings', 'mail-log'], queryFn: () => api.mailLog() });
  const [result, setResult] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const test = useMutation({
    mutationFn: () => api.mailTest(),
    onSuccess: ({ to }) => setResult({ tone: 'ok', text: `Sent to ${to}. Check that inbox, and the junk folder, in the next minute.` }),
    onError: (err) => setResult({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Could not send.' }),
  });

  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">Mail delivery</h2>
      <p className="mt-1 mb-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
        Whether email actually leaves this system. Every notification the app sends is listed here with what
        happened to its email.
      </p>

      {problem && (
        <div className="mb-4">
          <Alert>{problem} Add the missing line and restart the service.</Alert>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" onClick={() => test.mutate()} disabled={test.isPending || !mailEnabled}>
          {test.isPending ? 'Sending…' : 'Send me a test email'}
        </Button>
        {!mailEnabled && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Mail is switched off on the server (MAIL_ENABLED=false), so nothing can be sent.
          </span>
        )}
        {result && (
          <span className={`text-sm ${result.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-400'}`} role="status">
            {result.text}
          </span>
        )}
      </div>

      {log.data && (
        <>
          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            <Badge tone="success">{log.data.lastSevenDays.sent} sent in the last 7 days</Badge>
            <Badge tone={log.data.lastSevenDays.failed > 0 ? 'danger' : 'neutral'}>{log.data.lastSevenDays.failed} failed</Badge>
            {log.data.lastSevenDays.mailOff > 0 && (
              <Badge tone="warning">{log.data.lastSevenDays.mailOff} not sent: mail was off</Badge>
            )}
          </div>

          {log.data.entries.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">No notifications have been raised yet.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-2xl text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">To</th>
                    <th className="py-2 pr-4">What</th>
                    <th className="py-2">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {log.data.entries.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 last:border-b-0 dark:border-slate-800/70">
                      <td className="py-2 pr-4 whitespace-nowrap text-slate-600 dark:text-slate-400">
                        {new Date(row.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="text-slate-900 dark:text-slate-100">{row.to.displayName}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{row.to.email}</div>
                      </td>
                      <td className="max-w-md py-2 pr-4 text-slate-700 dark:text-slate-300">{row.title}</td>
                      <td className="py-2">
                        {row.emailedAt ? (
                          <Badge tone="success" dot>Sent</Badge>
                        ) : row.deliveryError ? (
                          <span className="inline-flex max-w-sm flex-col gap-1">
                            <Badge tone={row.deliveryError.startsWith('Mail is switched off') ? 'warning' : 'danger'} dot>
                              {row.deliveryError.startsWith('Mail is switched off') ? 'Not sent: mail off' : 'Failed'}
                            </Badge>
                            {!row.deliveryError.startsWith('Mail is switched off') && (
                              <span className="text-xs break-words text-red-700 dark:text-red-400">{row.deliveryError}</span>
                            )}
                          </span>
                        ) : (
                          <Badge tone="neutral">No email attempted</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** Something the server's configuration owns. Shown, never editable. */
function Fixed({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{note}</p>
      {/* Dashed, because it is a value rather than a control. */}
      <div className="mt-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
        {value}
      </div>
    </div>
  );
}
